import type { FormEvent } from 'react'
import type { AccountEmailStatus } from '@/lib/account-email'

import { CheckCircle2, Loader2, Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  AccountEmailError,
  accountEmailRequest,
  privateEmailLabel,
} from '@/lib/account-email'
import { gameStartButtonClassName } from '@/lib/game-start-button-style'
import { useI18n } from '@/lib/i18n'
import { returnTargetFrom } from '@/lib/return-target'

export function TournamentEmailEligibility({
  status,
  refresh,
  returnTarget,
  variant = 'tournament',
}: {
  status: AccountEmailStatus
  refresh: () => Promise<unknown>
  returnTarget?: string
  variant?: 'tournament' | 'game'
}) {
  const { language, t } = useI18n()
  const location = useLocation()
  const [email, setEmail] = useState(status.email ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cooldown, setCooldown] = useState<number | null>(null)

  useEffect(() => {
    setEmail(status.email ?? '')
    setError(null)
  }, [status.email])

  const submit = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault()
    setBusy(true)
    setError(null)
    setCooldown(null)
    try {
      await accountEmailRequest('request-account-email-verification', {
        email,
        language,
        returnTarget: returnTarget ?? returnTargetFrom(location),
      })
      await refresh()
    }
    catch (failure) {
      if (failure instanceof AccountEmailError) {
        if (failure.code === 'EMAIL_BINDING_UNAVAILABLE')
          setError('accountEmail.bindingUnavailable')
        else if (failure.code === 'EMAIL_RESEND_COOLDOWN')
          setCooldown(failure.seconds ?? null)
        else setError('forum.errorGeneric')
      }
      else {
        setError('forum.errorGeneric')
      }
    }
    finally {
      setBusy(false)
    }
  }

  const check = async () => {
    setBusy(true)
    setError(null)
    try {
      await refresh()
    }
    catch {
      setError('forum.errorGeneric')
    }
    finally {
      setBusy(false)
    }
  }

  if (status.state === 'EMAIL_VERIFIED' && status.email) {
    return (
      <div data-testid="tournament-email-verified" className="mt-4 text-start">
        <h3 className="mb-3 flex items-center gap-2 font-semibold">
          <Mail className="size-5" aria-hidden="true" />
          <span className="hidden sm:inline">{t('accountEmail.label')}</span>
          <span className="sm:hidden">{t('accountEmail.accountLabel')}</span>
        </h3>
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card p-4">
          <span className="min-w-0 break-all">{privateEmailLabel(status.email)}</span>
          <span role="status" className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="size-5" aria-hidden="true" />
            {t('accountEmail.verified')}
          </span>
        </div>
      </div>
    )
  }

  const sent = status.sentAt !== null
  const secondaryTextClassName = variant === 'game'
    ? 'text-[#fff4d9]/80'
    : 'text-muted-foreground'
  return (
    <div className="mt-4" data-testid="tournament-email-status" aria-live="polite">
      {variant === 'tournament' && (
        <>
          <Mail className="mx-auto mb-3 size-8 text-primary" aria-hidden="true" />
          <h3 className="text-lg font-semibold">
            {t(status.state === 'EMAIL_UNBOUND' ? 'tournament.emailCondition' : 'accountEmail.pendingTitle')}
          </h3>
        </>
      )}
      {(sent || variant === 'tournament') && (
        <p className={`mt-2 text-sm ${secondaryTextClassName}`} role="status">
          {t(sent ? 'accountEmail.sentTo' : 'accountEmail.bindingDescription')}
        </p>
      )}
      {sent && status.email && (
        <p className="mt-1 break-all">{privateEmailLabel(status.email)}</p>
      )}
      <form onSubmit={submit} className="mt-4 space-y-3 text-start">
        <label htmlFor="tournament-email" className={`block text-sm ${secondaryTextClassName}`}>
          {t('accountEmail.address')}
        </label>
        <Input
          id="tournament-email"
          data-testid="tournament-email-input"
          type="email"
          autoComplete="email"
          required
          maxLength={254}
          value={email}
          disabled={busy}
          aria-describedby={error || cooldown !== null ? 'tournament-email-error' : undefined}
          className="min-h-11 border-border bg-card text-foreground focus-visible:ring-ring"
          onChange={event => setEmail(event.target.value)}
        />
        {(error || cooldown !== null) && (
          <p id="tournament-email-error" role="alert" className="text-sm text-primary">
            {cooldown !== null ? t('play.retry.reason.retryCoolingDown', { seconds: cooldown }) : t(error!)}
          </p>
        )}
        <Button
          type="submit"
          disabled={busy}
          data-testid={sent && email === status.email ? 'tournament-email-resend' : 'tournament-email-send'}
          className={`${gameStartButtonClassName} min-h-12 w-full`}
        >
          {busy && <Loader2 className="me-2 size-4 animate-spin" aria-hidden="true" />}
          {t(sent && email === status.email ? 'accountEmail.resendVerification' : 'accountEmail.sendVerification')}
        </Button>
      </form>
      {status.state === 'EMAIL_UNVERIFIED' && (
        <button
          type="button"
          disabled={busy}
          data-testid="tournament-email-check"
          onClick={() => { void check() }}
          className="mt-2 min-h-11 rounded-md px-3 text-sm underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('accountEmail.checkVerification')}
        </button>
      )}
    </div>
  )
}
